import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Bot, Check, Edit3, RefreshCw, ShieldCheck, X } from "lucide-react";
import { api, post } from "./api";

type Dashboard = {
  tasks_today: number; queued_tasks: number; failed_tasks: number;
  support_cases_open: number; support_cases_resolved_ai: number; escalations: number;
  discovered_leads: number; qualified_leads: number; approvals_pending: number;
  security_alerts_open: number; ai_cost_microunits_today: number; outbox_lag_seconds: number;
};
type DashboardResponse = { dashboard: Dashboard; enabled: boolean; support_mode: string };
type Row = Record<string, unknown> & { id?: string; status?: string; created_at?: string };
type ListResponse = { items: Row[] };
type AcquisitionSource = {
  code: string; label: string; platform: string; public_url: string; bot_url: string;
  unique_bot_starts: number; trial_users: number; first_confirmed_paying_clients: number;
  net_revenue_minor: number; unknown_confirmation_payments: number;
  registration_observation_unknown: number;
};
type AcquisitionResponse = {
  discovery: {
    ready: boolean; missing: string[]; max_attempts_per_utc_day: number;
    reservation_microunits: number;
  };
  funnel: { sources: AcquisitionSource[] };
};
type CenterTab = "dashboard" | "acquisition" | "approvals" | "support-cases" | "leads" | "partners" | "campaigns" | "tasks" | "runs" | "knowledge" | "experiments" | "costs" | "feature-flags" | "settings" | "audit-log" | "evals" | "security-alerts";

const tabs: Array<[CenterTab, string]> = [
  ["dashboard", "Обзор"], ["acquisition", "Привлечение"], ["approvals", "Согласования"],
  ["support-cases", "Поддержка"], ["leads", "Лиды"], ["partners", "Партнёры"],
  ["campaigns", "Кампании"],
  ["tasks", "Задачи"], ["runs", "Запуски"], ["knowledge", "База знаний"],
  ["experiments", "Эксперименты"], ["costs", "Расходы"],
  ["feature-flags", "Флаги"], ["settings", "Настройки"],
  ["audit-log", "Аудит"], ["evals", "Evals"], ["security-alerts", "Безопасность"],
];

const label = (key: string) => ({
  tasks_today: "Задач сегодня", queued_tasks: "В очереди", failed_tasks: "Ошибки",
  support_cases_open: "Открытые обращения", support_cases_resolved_ai: "Решено AI",
  escalations: "Эскалации", discovered_leads: "Новые лиды", qualified_leads: "Квалифицированы",
  approvals_pending: "Ждут решения", security_alerts_open: "Security alerts",
  ai_cost_microunits_today: "Расходы AI сегодня, $", outbox_lag_seconds: "Outbox lag, сек",
}[key] || key);

const metricValue = (key: string, value: number) =>
  key === "ai_cost_microunits_today" ? (value / 1_000_000).toFixed(4) : value;

function safePreview(row: Row) {
  const blocked = new Set(["redacted_input", "safe_input", "structured_output", "requested_action"]);
  return Object.entries(row)
    .filter(([key, value]) => !blocked.has(key) && value !== null && typeof value !== "object")
    .slice(0, 8);
}

const discoveryReason = (key: string) => ({
  AI_PLATFORM_ENABLED: "AI-платформа выключена",
  AI_LEAD_HUNTER_ENABLED: "Поиск площадок выключен",
  AI_RESEARCH_ENABLED: "Публичное исследование выключено",
  AI_PUBLIC_DISCOVERY_ENABLED: "Нет разрешения на платный публичный поиск",
  openai_provider_configuration: "OpenAI не настроен",
  standard_openai_endpoint_required: "Требуется стандартный адрес OpenAI API",
  discovery_reservation_exceeds_run_budget: "Лимит одного запуска меньше резерва поиска",
  discovery_request_limits: "Лимиты запроса недостаточны для поиска",
  previous_search_requires_billing_review: "Нужно проверить расходы предыдущего поиска",
}[key] || `Требуется проверка: ${key}`);

function safeLinkUrl(value: unknown, httpsOnly = false): string | null {
  if (typeof value !== "string" || value.length > 2048 || !/^https?:\/\//i.test(value)
    || /[\s\\]/u.test(value)
    || [...value].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return null;
  try {
    const url = new URL(value);
    if (!url.hostname || url.username || url.password
      || (httpsOnly ? url.protocol !== "https:" : !["http:", "https:"].includes(url.protocol))) return null;
    return url.href;
  } catch { return null; }
}

function evidenceLinks(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const links = value.slice(0, 30).flatMap((item: unknown) => {
    if (!item || typeof item !== "object") return [];
    const url = safeLinkUrl((item as Record<string, unknown>).url);
    return url ? [url] : [];
  });
  return [...new Set(links)];
}

function AcquisitionView({ data }: { data: AcquisitionResponse }) {
  const { discovery, funnel } = data;
  return <section className="panel ai-center-list" aria-label="Привлечение клиентов">
    <article>
      <header><b>Публичный поиск площадок</b><i>{discovery.ready ? "Поиск настроен" : "Поиск пока недоступен"}</i></header>
      <p>Только исследование открытых Telegram/VK-площадок. Эта страница не запускает платные запросы, публикации или рассылки.</p>
      {discovery.missing.length > 0 && <ul>{discovery.missing.map(reason => <li key={reason}>{discoveryReason(reason)}</li>)}</ul>}
      <div>
        <span><small>Максимум попыток за сутки UTC</small>{discovery.max_attempts_per_utc_day}</span>
        <span><small>Резерв перед запросом, $</small>{(discovery.reservation_microunits / 1_000_000).toFixed(2)}</span>
      </div>
      <p>Готовность настройки не означает, что поиск уже выполнен. Найденная площадка — кандидат на проверку, не клиент и не разрешение на рекламу. Решения доступны в «Согласованиях».</p>
    </article>
    <article>
      <header><b>Фактическая воронка по источникам</b></header>
      <p>Считаются уникальные запуски бота, не клики и не охват. Пробный доступ означает создание подписки, не подтверждённое использование VPN.</p>
      <p>Платящие клиенты — только первые подтверждённые покупки. Выручка учитывает зарегистрированные возвраты; расходы не вычтены, это не прибыль. Исторические платежи без даты подтверждения исключены из денежных показателей.</p>
    </article>
    {funnel.sources.length ? funnel.sources.map(source => {
      const publicUrl = safeLinkUrl(source.public_url, true);
      const botUrl = safeLinkUrl(source.bot_url, true);
      return <article key={source.code} aria-label={source.label}>
        <header><b>{source.label}</b><i>{source.platform}</i></header>
        <div>
          <span><small>Источник</small>{source.code}</span>
          {publicUrl && <span><a href={publicUrl} target="_blank" rel="noopener noreferrer">Открыть площадку</a></span>}
          {botUrl && <span><a href={botUrl} target="_blank" rel="noopener noreferrer">Ссылка на бота</a></span>}
          <span><small>Уникальные запуски бота</small>{source.unique_bot_starts}</span>
          <span><small>Пробные подписки</small>{source.trial_users}</span>
          <span><small>Первые подтверждённые платящие клиенты</small>{source.first_confirmed_paying_clients}</span>
          <span><small>Выручка после возвратов, ₽ (не прибыль)</small>{(source.net_revenue_minor / 100).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <span><small>Платежи с неизвестной датой подтверждения</small>{source.unknown_confirmation_payments}</span>
          <span><small>Неизвестно, была ли регистрация новой</small>{source.registration_observation_unknown}</span>
        </div>
      </article>;
    }) : <p className="web-admin-empty"><ShieldCheck />Зарегистрированных источников пока нет. Это не показатель числа клиентов.</p>}
  </section>;
}

export function AITeamCenter() {
  const [tab, setTab] = useState<CenterTab>("dashboard");
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [acquisition, setAcquisition] = useState<AcquisitionResponse | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true); setError("");
    try {
      if (tab === "dashboard") {
        setDashboard(await api<DashboardResponse>("/admin/ai/dashboard")); setRows([]);
      } else if (tab === "acquisition") {
        setAcquisition(null);
        setAcquisition(await api<AcquisitionResponse>("/admin/ai/acquisition")); setRows([]);
      } else if (tab === "costs") {
        const result = await api<Record<string, unknown>>("/admin/ai/costs");
        setRows([{ id: "costs", ...result }]);
      } else if (tab === "feature-flags") {
        setRows((await api<ListResponse>("/admin/ai/feature-flags")).items);
      } else {
        const suffix = tab === "approvals" ? "?status=pending" : tab === "security-alerts" ? "?status=open" : "";
        setRows((await api<ListResponse>(`/admin/ai/${tab}${suffix}`)).items);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось загрузить AI Team Center.");
    } finally { setBusy(false); }
  }, [tab]);
  useEffect(() => { void load(); }, [load]);

  const decide = async (id: string, decision: "approved" | "rejected", edited_payload?: Record<string, unknown>) => {
    if (!window.confirm(decision === "approved" ? "Подтвердить это действие?" : "Отклонить действие?")) return;
    try { await post(`/admin/ai/approvals/${id}/decision`, { decision, edited_payload }); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Решение не сохранено."); }
  };

  const editAndApprove = async (row: Row) => {
    if (!row.id) return;
    const initial = JSON.stringify(row.requested_action || {}, null, 2);
    const edited = window.prompt("Измените JSON действия перед подтверждением", initial);
    if (edited === null) return;
    try { await decide(row.id, "approved", JSON.parse(edited) as Record<string, unknown>); }
    catch { setError("Исправьте JSON и повторите."); }
  };

  const setFlag = async (row: Row, value: string) => {
    if (!row.key) return;
    try {
      await api(`/admin/ai/feature-flags/${String(row.key)}`, {
        method: "PUT", body: JSON.stringify({ value }),
      });
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Флаг не сохранён."); }
  };

  return <div className="ai-center">
    <section className="panel ai-center-hero"><Bot /><div><h2>AI Team Center</h2><p>Наблюдение, согласования, качество, расходы и безопасный rollout.</p></div>{dashboard && <i className={dashboard.enabled ? "active" : "off"}>{dashboard.enabled ? dashboard.support_mode : "выключено"}</i>}</section>
    <nav className="ai-center-tabs">{tabs.map(([value, title]) => <button key={value} className={tab === value ? "active" : ""} onClick={() => setTab(value)}>{title}</button>)}</nav>
    {error && <div className="error-box"><AlertTriangle />{error}<button onClick={() => void load()}>Повторить</button></div>}
    {busy ? <div className="web-admin-loading"><RefreshCw className="spin" />Обновляю AI Team Center</div> : tab === "dashboard" && dashboard ?
      <section className="ai-center-metrics">{Object.entries(dashboard.dashboard).map(([key, value]) => <article className="panel" key={key}><small>{label(key)}</small><b>{metricValue(key, value)}</b></article>)}</section> :
      tab === "acquisition" ? (acquisition && <AcquisitionView data={acquisition} />) :
      <section className="panel ai-center-list">{rows.length ? rows.map((row, index) => <article key={String(row.id || index)}>
        <header><b>{String(row.action_type || row.intent || row.agent_key || row.document_id || row.setting || row.id || "Запись")}</b><i>{String(row.status || row.final_status || "")}</i></header>
        <div>{safePreview(row).map(([key, value]) => <span key={key}><small>{key}</small>{String(value)}</span>)}</div>
        {tab === "approvals" && <>
          <div aria-label="Ссылки на источники">{evidenceLinks(row.evidence).map(url => <span key={url}><a href={url} target="_blank" rel="noopener noreferrer">{url}</a></span>)}</div>
          <pre>{JSON.stringify({ requested_action: row.requested_action, evidence: row.evidence, risk_flags: row.risk_flags }, null, 2)}</pre>
        </>}
        {tab === "approvals" && row.status === "pending" && row.id && <footer><button onClick={() => void decide(row.id!, "rejected")}><X />Отклонить</button><button onClick={() => void editAndApprove(row)}><Edit3 />Изменить</button><button className="primary" onClick={() => void decide(row.id!, "approved")}><Check />Подтвердить</button></footer>}
        {tab === "feature-flags" && row.key !== "AI_SUPPORT_MODE" && <footer><button onClick={() => void setFlag(row, "0")}><X />Off</button><button className="primary" onClick={() => void setFlag(row, "1")}><Check />On</button></footer>}
      </article>) : <p className="web-admin-empty"><ShieldCheck />Записей пока нет.</p>}</section>}
  </div>;
}
